import { supabase } from "@/integrations/supabase/client";
import { SERVICES, type ServiceType } from "@/lib/services";
import { LISTING_TABLE, type ListingStatus } from "@/lib/listings";
import type { Enquiry } from "@/lib/enquiries";
import { normalizeStatus } from "@/lib/enquiries";

/* ------------------------------------------------------------------ *
 * Partner analytics — counts only. No revenue is calculated anywhere.
 * ------------------------------------------------------------------ */

export const ANALYTICS_SERVICES: ServiceType[] = SERVICES.map((s) => s.value);

export interface MonthlyPoint {
  month: string; // "2026-07"
  label: string; // "Jul"
  count: number;
}

export interface ServiceBreakdown {
  service: ServiceType;
  label: string;
  total: number;
  active: number;
  pending: number;
}

export interface PartnerAnalytics {
  totals: {
    listings: number;
    active: number;
    pending: number;
    enquiries: number;
    contactRequests: number;
    views: number;
  };
  byService: ServiceBreakdown[];
  monthlyEnquiries: MonthlyPoint[];
  monthlyViews: MonthlyPoint[];
  latestEnquiries: Enquiry[];
}

const MONTH_LABELS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/** Last 6 months, oldest first. */
function monthBuckets(months = 6): MonthlyPoint[] {
  const now = new Date();
  const out: MonthlyPoint[] = [];
  for (let i = months - 1; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push({
      month: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`,
      label: MONTH_LABELS[d.getMonth()],
      count: 0,
    });
  }
  return out;
}

function monthKey(iso: string): string {
  return iso.slice(0, 7);
}

function tally(buckets: MonthlyPoint[], rows: { created_at: string }[]): MonthlyPoint[] {
  const map = new Map(buckets.map((b) => [b.month, b]));
  for (const r of rows) {
    const b = map.get(monthKey(r.created_at));
    if (b) b.count += 1;
  }
  return buckets;
}

export function windowStartIso(months = 6): string {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth() - (months - 1), 1).toISOString();
}

interface ListingRow {
  id: string;
  status: ListingStatus;
  created_at: string;
}

/**
 * Loads every analytics figure for one partner, optionally restricted to a
 * subset of services (Sale / Rent / Lease / Parking filters).
 */
export async function fetchPartnerAnalytics(
  partnerId: string,
  services: ServiceType[] = ANALYTICS_SERVICES,
): Promise<PartnerAnalytics> {
  const active = services.length > 0 ? services : ANALYTICS_SERVICES;
  const since = windowStartIso();

  const listingResults = await Promise.all(
    active.map(async (service) => {
      const { data, error } = await supabase
        .from(LISTING_TABLE[service])
        .select("id,status,created_at")
        .eq("owner_id", partnerId);
      if (error) throw error;
      return { service, rows: (data as unknown as ListingRow[]) ?? [] };
    }),
  );

  const listingIds = listingResults.flatMap((r) => r.rows.map((row) => row.id));

  const [enquiryRes, contactRes, viewRes, latestRes] = await Promise.all([
    supabase
      .from("partner_enquiries")
      .select("id,created_at,service_type")
      .eq("partner_id", partnerId)
      .in("service_type", active),
    supabase
      .from("contact_unlock_requests")
      .select("id,listing_id,created_at")
      .eq("owner_id", partnerId),
    supabase
      .from("listing_views")
      .select("id,created_at,service_type")
      .eq("owner_id", partnerId)
      .in("service_type", active),
    supabase
      .from("partner_enquiries")
      .select("*")
      .eq("partner_id", partnerId)
      .in("service_type", active)
      .order("created_at", { ascending: false })
      .limit(10),
  ]);

  const enquiries = (enquiryRes.data as unknown as { created_at: string }[]) ?? [];
  const views = (viewRes.data as unknown as { created_at: string }[]) ?? [];
  const contactRows =
    (contactRes.data as unknown as { listing_id: string | null; created_at: string }[]) ?? [];
  const contactRequests =
    active.length === ANALYTICS_SERVICES.length
      ? contactRows.length
      : contactRows.filter((r) => r.listing_id && listingIds.includes(r.listing_id)).length;

  const byService: ServiceBreakdown[] = listingResults.map(({ service, rows }) => ({
    service,
    label: SERVICES.find((s) => s.value === service)?.label ?? service,
    total: rows.length,
    active: rows.filter((r) => r.status === "published").length,
    pending: rows.filter((r) => r.status === "pending").length,
  }));

  const latestEnquiries = ((latestRes.data as unknown as Enquiry[]) ?? []).map((e) => ({
    ...e,
    enquiry_status: normalizeStatus(e.enquiry_status as unknown as string),
  }));

  return {
    totals: {
      listings: byService.reduce((s, b) => s + b.total, 0),
      active: byService.reduce((s, b) => s + b.active, 0),
      pending: byService.reduce((s, b) => s + b.pending, 0),
      enquiries: enquiries.length,
      contactRequests,
      views: views.length,
    },
    byService,
    monthlyEnquiries: tally(
      monthBuckets(),
      enquiries.filter((e) => e.created_at >= since),
    ),
    monthlyViews: tally(
      monthBuckets(),
      views.filter((v) => v.created_at >= since),
    ),
    latestEnquiries,
  };
}

/** Fire-and-forget view tracking from the property details page. */
export async function recordListingView(input: {
  listingId: string;
  serviceType: ServiceType;
  ownerId: string | null;
  viewerId: string | null;
}): Promise<void> {
  if (!input.ownerId) return;
  const key = `landoo:viewed:${input.listingId}`;
  try {
    if (typeof window !== "undefined") {
      const last = window.sessionStorage.getItem(key);
      if (last) return;
      window.sessionStorage.setItem(key, "1");
    }
  } catch {
    /* storage unavailable — still record the view */
  }
  await supabase.from("listing_views").insert({
    listing_id: input.listingId,
    service_type: input.serviceType,
    owner_id: input.ownerId,
    viewer_id: input.viewerId,
  });
}
